import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { CalendarClock } from "lucide-react";

const CATEGORY_LABELS = {
  recruitment: "Recruitment", career: "Career", cv: "CV", cv_marketing_package: "CV marketing package",
  employer_recruitment: "Employer recruitment", education: "Education", course: "Course",
  document_verification: "Document verification", visa_administrative_support: "Visa administrative support",
  regulated_immigration: "Regulated immigration", partner: "Partner",
  white_label_demonstration: "White-label demonstration", chargeable_technical_support: "Technical support",
};

export default function Consultations() {
  const { user } = useAuth();
  return <ConsultationPortal key={JSON.stringify([user?.id, user?.tenant_id])} user={user} />;
}

function ConsultationPortal({ user }) {
  const [consultants, setConsultants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [category, setCategory] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [terms, setTerms] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [savedBooking, setSavedBooking] = useState(null);
  const actionPending = useRef(false);
  const loadVersion = useRef(0);
  const availabilityVersion = useRef(0);
  const load = useCallback(async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError("");

    try {
      const requests = [
        api.get("/consultants"),
        api.get("/consultations/categories"),
      ];

      if (user) requests.push(api.get("/consultations/bookings/mine"));

      const [consultantResponse, categoryResponse, bookingResponse] = await Promise.all(requests);

      if (version !== loadVersion.current) return;
      setConsultants(consultantResponse.data);
      setCategories(categoryResponse.data.categories);

      if (bookingResponse) setBookings(bookingResponse.data);
    } catch (error) {
      if (version === loadVersion.current) setLoadError(applicationErrorMessage(error, "Consultation information could not be loaded. Please try again."));
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  }, [user]);
  const invalidateLoad = useCallback(() => { ++loadVersion.current; ++availabilityVersion.current; }, []);
  useEffect(() => { load(); return invalidateLoad; }, [load, invalidateLoad]);

  const choose = async (consultant) => {
    if (actionPending.current) return;
    const version = ++availabilityVersion.current;
    setSelected(consultant); setSlot(""); setSlots([]); setTerms(false); setSavedBooking(null);
    setMsg(""); setCategory(consultant.categories[0] || "");
    setAvailabilityLoading(true); setAvailabilityError("");
    try {
      const { data } = await api.get(`/consultants/${consultant.id}/availability`);
      if (version === availabilityVersion.current) setSlots(data.slots || []);
    } catch (error) {
      if (version === availabilityVersion.current) setAvailabilityError(applicationErrorMessage(error, "Unable to load available times. Please try again."));
    } finally { if (version === availabilityVersion.current) setAvailabilityLoading(false); }
  };

  const bookAndPay = async () => {
    if (actionPending.current) return;
    if (!user) { setMsg("Please sign in to book a consultation."); return; }
    if (!selected || !slot || !terms) { setMsg("Choose an available time and accept the consultation terms."); return; }
    actionPending.current = true; setBusy(true); setMsg("");
    let booking = savedBooking;
    try {
      if (!booking) {
        const response = await api.post("/consultations/bookings", {
          consultant_id: selected.id, category, start_at: slot,
          duration_minutes: selected.durations[0], meeting_mode: selected.meeting_modes[0],
          terms_version: "v1", accepted_terms: terms, idempotency_key: `${selected.id}-${slot}`,
        });
        booking = response.data; setSavedBooking(booking);
        setBookings((items) => [...items.filter((item) => item.id !== booking.id), booking]);
      }
      const { data: checkout } = await api.post("/payments/checkout", {
        item_type: "consultation_booking", item_id: booking.id, origin_url: window.location.origin,
      });
      if (!checkout.checkout_url) throw new Error("Secure payment could not be opened.");
      window.location.href = checkout.checkout_url;
    } catch (error) {
      setMsg((booking ? "Your booking is awaiting payment. Use Retry payment below; the appointment is confirmed after payment. " : "") + applicationErrorMessage(error, "Unable to start the booking. Please try again."));
    } finally { actionPending.current = false; setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="consultations-page">
      <p className="overline">Book a consultation</p>
      <h1 className="mt-3 text-3xl sm:text-4xl font-semibold">Paid sessions with a EuropEdu consultant</h1>
      <p className="mt-4 max-w-3xl text-sm text-slate-600">
        All consultations are paid sessions unless an administrator creates a promotion. Payment is taken in
        advance and an appointment is only confirmed once payment is confirmed. Only authorised regulated
        advisers can accept regulated immigration bookings, and their regulatory details are always displayed.
      </p>

      {consultants.length > 0 && (
        <section className="card p-6 mt-8" data-testid="consultation-categories">
          <h2 className="text-lg font-semibold">Available consultation categories</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {categories
              .filter((categoryName) => consultants.some((consultant) => consultant.categories.includes(categoryName)))
              .map((categoryName) => (
                <span key={categoryName} className="pill bg-blue-50 text-blue-800">
                  {CATEGORY_LABELS[categoryName] || categoryName}
                </span>
              ))}
          </div>
        </section>
      )}

      {loading ? (
        <section className="card p-8 mt-8 text-center" data-testid="consultants-loading">
          <CalendarClock className="w-7 h-7 mx-auto text-[#1d4ed8]" />
          <p className="mt-4 text-sm text-slate-600">Loading consultation availability…</p>
        </section>
      ) : loadError ? (
        <section className="card p-8 mt-8 text-center" data-testid="consultants-error">
          <CalendarClock className="w-7 h-7 mx-auto text-red-600" />
          <p role="alert" className="mt-4 text-sm text-red-700">{loadError}</p>
          <button className="btn btn-ghost mt-4" onClick={load}>Retry loading</button>
        </section>
      ) : consultants.length === 0 ? (
        <section className="card p-8 mt-8 text-center" data-testid="no-consultants-panel">
          <CalendarClock className="w-7 h-7 mx-auto text-[#1d4ed8]" />
          <h2 className="mt-4 text-xl font-semibold">Consultations are currently available by request</h2>
          <p className="mt-3 text-sm text-slate-600 max-w-xl mx-auto">
            Consultant profiles are published by EuropEdu administrators from the admin console, so that
            qualifications and regulatory details are always confirmed by a person before any session can be
            booked. No profiles are published at the moment.
          </p>
          <p className="mt-4 text-sm text-slate-700">
            To request a consultation, email <a className="underline font-semibold" href="mailto:contact@europedu.co.uk">contact@europedu.co.uk</a> or
            ask the EuropEdu Support Assistant to raise a request.
          </p>
          <Link to="/dashboard" className="btn btn-ghost mt-6" data-testid="no-consultants-dashboard-link">Go to your dashboard</Link>
        </section>
      ) : (
        <>
          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="consultant-list">
            {consultants.map((c) => (
              <button key={c.id} disabled={busy} onClick={() => choose(c)} data-testid={`consultant-${c.id}`}
                className={`card p-5 text-left ${selected?.id === c.id ? "border-[#1e3a8a]" : ""}`}>
                <h2 className="font-semibold">{c.name}</h2>
                <p className="text-sm text-slate-600">{c.title} · {money(c.price_pence, c.currency)}</p>
                <p className="text-xs text-slate-500 mt-2">Categories: {c.categories.map((x) => CATEGORY_LABELS[x] || x).join(", ")}</p>
                {c.regulatory_details && <p className="text-xs text-slate-500 mt-1">Regulatory details: {c.regulatory_details}</p>}
              </button>
            ))}
          </div>

          {selected && (
            <section className="card p-6 mt-8" data-testid="booking-panel">
              <h2 className="text-lg font-semibold">Available times ({Intl.DateTimeFormat().resolvedOptions().timeZone})</h2>
              <label className="block text-sm mt-4">Consultation category
                <select disabled={busy || !!savedBooking} data-testid="booking-category" className="field mt-1" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {selected.categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                </select>
              </label>
              {availabilityLoading && <p role="status" className="mt-4 text-sm">Loading available times…</p>}
              {availabilityError && <div className="mt-4"><p role="alert" className="text-sm text-red-700">{availabilityError}</p><button className="btn btn-ghost mt-3" onClick={() => choose(selected)}>Retry availability</button></div>}
              {!availabilityLoading && !availabilityError && slots.length === 0 && <p className="mt-4 text-sm">No appointment times are available for this consultant.</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button key={s.start_at} disabled={!s.available || busy || !!savedBooking} data-testid={`slot-${s.start_at}`}
                    onClick={() => setSlot(s.start_at)}
                    className={`pill border ${slot === s.start_at ? "bg-[#1e3a8a] text-white" : s.available ? "bg-white text-slate-700" : "bg-slate-100 text-slate-400 line-through"}`}>
                    {new Date(s.start_at).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </button>
                ))}
              </div>
              <label className="flex gap-2 items-start text-sm mt-5">
                <input disabled={busy || !!savedBooking} data-testid="accept-terms-checkbox" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
                I accept the versioned consultation terms, including the rescheduling, cancellation and refund policy.
              </label>
              <button data-testid="book-button" disabled={!slot || !terms || busy || availabilityLoading || !!availabilityError} onClick={bookAndPay} className="btn btn-primary mt-4">
                {busy ? "Opening secure payment…" : savedBooking ? "Retry payment" : `Pay ${money(selected.price_pence, selected.currency)} and confirm`}
              </button>
              <p className="mt-2 text-xs text-slate-500">Payments are processed by Stripe in test mode. Use card 4242 4242 4242 4242 with any future expiry and CVC.</p>
            </section>
          )}
        </>
      )}

      {bookings.length > 0 && (
        <section className="card p-6 mt-8" data-testid="my-bookings">
          <h2 className="text-lg font-semibold">Your appointments</h2>
          <ul className="mt-3 text-sm space-y-1">
            {bookings.map((b) => (
              <li key={b.id}>{new Date(b.start_at).toLocaleString("en-GB")} · {b.consultant_name} · {b.status.replace(/_/g, " ")} · {money(b.price_pence)}</li>
            ))}
          </ul>
        </section>
      )}
      {msg && <p role="alert" data-testid="booking-message" className="mt-4 text-sm text-red-700">{msg}</p>}
    </div>
  );
}
